import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useParams } from 'react-router-dom';
import {
  getPublicParishLanding,
  type PublicParishLandingPayload,
} from '../../services/parishLandingService';
import { getBrandConfig, resolveBrandKey } from '../../brands/registry';

const templateLoaders: Record<
  string,
  () => Promise<{ default: React.ComponentType<{ data: PublicParishLandingPayload }> }>
> = {
  events_bio: () => import('./templates/EventsBioLandingPage'),
  comedy_club: () => import('./templates/ComedyClubLandingPage'),
};

function resolveLandingTemplate(data: PublicParishLandingPayload): string {
  const key = resolveBrandKey({
    publicBrandKey: data.brandKey,
    legacyLabel: `${data.parishSlug || ''} ${data.parishName || ''}`,
  });
  const brand = getBrandConfig(key);
  // Marca white-label com template próprio tem prioridade sobre o salvo no landing.
  if (key !== 'platform' && brand.landingTemplate) {
    return brand.landingTemplate;
  }
  if (data.landing?.template === 'comedy_club') return 'comedy_club';
  return data.landing?.template || brand.landingTemplate || 'events_bio';
}

interface Props {
  /** Quando já resolvido pelo host custom */
  initialData?: PublicParishLandingPayload | null;
}

const LandingPublicPage: React.FC<Props> = ({ initialData = null }) => {
  const { parishSlug } = useParams<{ parishSlug?: string }>();
  const [loading, setLoading] = useState(!initialData);
  const [data, setData] = useState<PublicParishLandingPayload | null>(initialData);
  const [Template, setTemplate] = useState<React.ComponentType<{
    data: PublicParishLandingPayload;
  }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exclusiveDomain, setExclusiveDomain] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
      setLoading(false);
      return;
    }

    if (!parishSlug) {
      setError('Página não encontrada.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void getPublicParishLanding(parishSlug)
      .then((payload) => {
        if (cancelled) return;
        if (!payload) {
          setError('Página não encontrada ou ainda não publicada.');
          setLoading(false);
          return;
        }
        setData(payload);
        setLoading(false);
      })
      .catch((err: Error & { exclusiveDomain?: string }) => {
        if (cancelled) return;
        if (err?.message === 'EXCLUSIVE_DOMAIN' && err.exclusiveDomain) {
          setExclusiveDomain(err.exclusiveDomain);
          setError('Esta página está disponível apenas no domínio personalizado.');
        } else {
          setError('Página não encontrada ou ainda não publicada.');
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialData, parishSlug]);

  useEffect(() => {
    if (!data) return;
    const key = resolveLandingTemplate(data);
    const loader = templateLoaders[key] || templateLoaders.events_bio;
    let cancelled = false;
    void loader().then((mod) => {
      if (!cancelled) setTemplate(() => mod.default);
    });
    return () => {
      cancelled = true;
    };
  }, [data]);

  if (loading || (data && !Template)) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data || !Template) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center', p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: exclusiveDomain ? 2 : 0 }}>
          {error || 'Página não encontrada.'}
        </Typography>
        {exclusiveDomain && (
          <Button
            variant="contained"
            href={exclusiveDomain}
            target="_blank"
            rel="noreferrer"
            startIcon={<OpenInNewIcon />}
          >
            Abrir {exclusiveDomain.replace(/^https?:\/\//, '')}
          </Button>
        )}
      </Box>
    );
  }

  return <Template data={data} />;
};

export default LandingPublicPage;
